import { Colors, DATE_FORMATS, formatDate, ItemList, Text } from '@amelio/ui-library';

import { EmployeeIdentity } from './EmployeeIdentity';

import type { OverdueEmployee } from '@/types/thematic-meetings';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { toDateInputLang } from '@/utils/dateUtils';
import { getFullName } from '@/utils/user';

interface OverdueEmployeeListProps {
    employees: OverdueEmployee[];
    onSelectEmployee: (employeeId: string) => void;
    className?: string;
}

export const OverdueEmployeeList = ({
    employees,
    onSelectEmployee,
    className,
}: OverdueEmployeeListProps) => {
    const { t } = useAmelioTranslations();
    // `fr_FR`, the language a French-from-France company reads in, is not a locale the library's
    // date helpers know: left as it is, the date comes back in English.
    const dateLanguage = toDateInputLang(useSelectedAppLanguage());

    return (
        <ItemList
            variant={'divided'}
            className={className}
            dataTestId={'overdue-employees'}
            items={employees.map((employee) => ({
                id: employee.id,
                content: (
                    <EmployeeIdentity
                        employee={employee}
                        size={'2xs'}
                        nameSize={'default'}
                        nameWeight={'semibold'}
                        showJobTitle
                    />
                ),
                meta: (
                    <Text
                        as={'span'}
                        color={employee.lastMeetingAt ? Colors.BASE_500 : Colors.WARNING_900}
                        weight={employee.lastMeetingAt ? undefined : 'medium'}
                        className={'text-xs'}
                        noWrap
                    >
                        {employee.lastMeetingAt
                            ? t('thematicMeetings.overdue.lastMeetingOn', {
                                  date: formatDate({
                                      date: employee.lastMeetingAt,
                                      format: DATE_FORMATS.DISPLAY_DATE,
                                      language: dateLanguage,
                                  }),
                              })
                            : t('thematicMeetings.overdue.noMeeting')}
                    </Text>
                ),
                onClick: () => onSelectEmployee(employee.id),
                ariaLabel: t('thematicMeetings.overdue.scheduleMeeting', {
                    name: getFullName(employee),
                }),
            }))}
        />
    );
};
